import { Box, Grid, Typography } from "@mui/material";
import { NewCampaignContext } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect } from "react";
import { CustomEvent, Map } from "schemas/functions";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import FollowupSelect from "components/TemplateScreen/FollowupSelect";
import OutreachSelect from "components/TemplateScreen/OutreachSelect";
import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";

import InfoBox from "../../SharedComponents/InfoBox";
import StepperFooter from "../../SharedComponents/StepperFooter";
import styles from "./styles";

interface Props {
  handleUpdateCampaign: (body: Map) => void;
  moveBack: () => void;
  submitLoading?: boolean;
  errorMessage?: string;
}

const CampaignTemplates = ({
  handleUpdateCampaign,
  moveBack,
  submitLoading = false,
  errorMessage = "",
}: Props) => {
  const { emailSettings } = useContext(OrganizationUserContext);
  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    setCreateTemplateDialogOpen,
    setCurrentTemplateContentType,
    fetchOutreachTemplates,
  } = useContext(OutreachTemplatesContext);
  const { templates: selectedTemplates, setTemplates: setSelectedTemplates } =
    useContext(NewCampaignContext);
  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const templateFromEmailSettings = emailSettings?.emailSettingTemplates?.map(
    (template) => template.templateId,
  );
  const selectedFollowups = selectedTemplates?.slice(
    1,
    selectedTemplates?.length,
  );

  const handleGenerateTemplate = (contentType: TemplateContentType) => {
    setCurrentTemplateContentType(contentType);
    setCreateTemplateDialogOpen(true);
  };

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const copy = [...selectedTemplates];
    const template = allTemplates?.find((x) => x.id === value);
    if (template) {
      copy[index] = template;
      setSelectedTemplates(copy);
    }
  };

  const addFollowUp = () => {
    setSelectedTemplates([...selectedTemplates, followupTemplates?.[0]]);
  };

  const removeFollowUp = (index: number) => {
    const copy = [...selectedTemplates];
    copy.splice(index, 1);
    setSelectedTemplates(copy);
  };

  useEffect(() => {
    if (allTemplates?.length === 0) {
      fetchOutreachTemplates();
      return;
    }

    if (selectedTemplates.length > 0) return;

    const presetTemplates = emailSettings?.emailSettingTemplates || [];
    const matchedTemplates: Template[] = [];
    for (const settingTemplate of presetTemplates) {
      const template = allTemplates?.find(
        (x) => x.id === settingTemplate.templateId,
      );
      if (template) matchedTemplates.push(template);
    }
    if (matchedTemplates.length > 0) {
      setSelectedTemplates(matchedTemplates);
      return;
    }

    if (filteredOutreachTemplates?.length > 0) {
      setSelectedTemplates([filteredOutreachTemplates[0]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTemplates?.length, emailSettings?.emailSettingTemplates?.length]);

  return (
    <>
      <InfoBox
        icon={
          errorMessage
            ? "fa-regular fa-circle-exclamation"
            : "fa-regular fa-envelope"
        }
        title={errorMessage || "Craft your outreach email"}
        description={
          errorMessage
            ? "Please fix this error to proceed"
            : "Start with a template or write from scratch. Use {{brand_name}} and {{first_name}} to personalize your email."
        }
        error={!!errorMessage}
      />

      <Grid
        container
        flexDirection="column"
        sx={{ mb: 2, px: { xs: 2, md: 4 }, maxWidth: 600 }}
        gap={2}
      >
        <Box>
          <Typography color="textSecondary" sx={{ mb: 2 }}>
            Select or create your outreach template
          </Typography>

          {selectedTemplates[0] ? (
            <OutreachSelect
              templates={selectedTemplates}
              handleChange={handleTemplateChange}
              filteredOutreachTemplates={filteredOutreachTemplates}
              templateFromEmailSettings={templateFromEmailSettings}
              addNewOption={() =>
                handleGenerateTemplate(TemplateContentType.OUTREACH)
              }
            />
          ) : (
            <StyledLargeButton
              onClick={() =>
                handleGenerateTemplate(TemplateContentType.OUTREACH)
              }
              disableElevation
            >
              Create Template
            </StyledLargeButton>
          )}
        </Box>

        <Box>
          <Typography color="textSecondary" sx={{ mb: 2 }}>
            Select or add follow-ups (optional)
          </Typography>
          {followupTemplates.length === 0 ? (
            <StyledLargeButton
              disableElevation
              disabled={outreachTemplates.length === 0}
              onClick={() =>
                handleGenerateTemplate(TemplateContentType.FOLLOWUP)
              }
            >
              Create Follow-up
            </StyledLargeButton>
          ) : (
            <Grid container gap={2} flexDirection="column">
              {selectedFollowups?.length > 0 &&
                selectedFollowups.map((followup, index) => (
                  <FollowupSelect
                    key={index}
                    followup={followup}
                    index={index}
                    filteredFollowUps={filteredFollowUps}
                    templateFromEmailSettings={templateFromEmailSettings}
                    handleTemplateChange={(e: CustomEvent, idx: number) =>
                      handleTemplateChange(e, idx)
                    }
                    onRemove={() => removeFollowUp(index + 1)}
                  />
                ))}

              {followupTemplates?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Sent 4 business days after previous email
                  </Typography>
                </Grid>
              )}

              {followupTemplates?.length > 0 &&
                selectedTemplates.length < MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
                  <Grid item xs={12}>
                    <StyledLargeButton
                      variant="outlined"
                      disableElevation
                      onClick={addFollowUp}
                      styles={styles.addButton}
                    >
                      <Box
                        component="i"
                        className="fa-regular fa-add"
                        sx={{ mr: 0.5 }}
                      />{" "}
                      Follow Up
                    </StyledLargeButton>
                  </Grid>
                )}
            </Grid>
          )}
        </Box>
      </Grid>

      <StepperFooter
        handleBack={moveBack}
        handleNext={() => {
          const templateIds = selectedTemplates.map((t) => t.id);
          handleUpdateCampaign({ templateIds: templateIds });
        }}
        nextDisabled={selectedTemplates.length === 0}
        nextLoading={submitLoading}
      />
    </>
  );
};

export default CampaignTemplates;
